'use strict';
import keycode from 'keycode';
import assign from 'lodash/assign';
import React, {Component} from 'react';
import PropTypes from 'prop-types';
import prefix from 'react-prefixr';
import classNames from 'classnames';

const defaultTransition = {
  property: 'all',
  duration: 300,
  timingfunction: 'linear',
};

export function ModalHOC(ModalComponent, BodyComponent) {
  return class extends Component {
    static propTypes = {
      body: PropTypes.any.isRequired,
      isOnTop: PropTypes.bool.isRequired,
      onSuccess: PropTypes.func.isRequired,
      onCancel: PropTypes.func.isRequired,
      backdropClickToClose: PropTypes.bool,
      hardwareBackButtonClose: PropTypes.bool,
      effect: PropTypes.object.isRequired,
      modalProps: PropTypes.object.isRequired,
    };

    static defaultProps = {
      backdropClickToClose: true,
      hardwareBackButtonClose: true,
      effect: {
        transition: defaultTransition,
      },
      modalProps: {},
    };

    constructor(props) {
      super(props);
      this.state = {
        open: false,
      };
      this.handleSuccess = this.handleSuccess.bind(this);
      this.handleCancel = this.handleCancel.bind(this);
      this.handleBack = this.handleBack.bind(this);
      this.handleKeyDown = this.handleKeyDown.bind(this);
      this.handleClickBackdrop = this.handleClickBackdrop.bind(this);
      this.handleSubmit = this.handleSubmit.bind(this);
    }

    handleClose(callback) {
      if (!this.hasOpened) {
        // Close immediately if the modal has not yet started the open transition
        callback();

      } else if (this.state.open) {
        const transitionTimeMS = this.getTransitionDuration();
        this.setState({open: false}, () => {
          this.closeTimer = setTimeout(callback, transitionTimeMS);
        });
      }
    }

    handleSuccess(result) {
      const onSuccess = this.props.onSuccess;
      this.handleClose(() => onSuccess(result));
    }

    handleCancel(reason) {
      const onCancel = this.props.onCancel;
      this.handleClose(() => onCancel(reason));
    }

    handleKeyDown(e) {
      if (keycode(e) === 'esc')
        this.handleBack(e);
    }

    handleBack(e) {
      e.stopPropagation();
      e.preventDefault();
      if (this.props.hardwareBackButtonClose && this.props.isOnTop)
        this.handleCancel();
    }

    handleClickBackdrop() {
      if (this.props.backdropClickToClose)
        this.handleCancel();
    }

    /**
     * Generic submit method when modals are forms.
     * Only cancels default browser behavior.
     * Modal content components are free to override this behavior or ignore this prop if they are not <form> elements.
     * @param event
     */
    handleSubmit(event) {
      event.preventDefault();
      event.stopPropagation();
    }

    componentDidMount() {
      // cordova hardware backbutton support
      document.addEventListener('backbutton', this.handleBack);
      document.body.addEventListener('keydown', this.handleKeyDown);
      // delay opening so that the initial contents can render prior to the animation starting
      setTimeout(() => this.setState(
        {open: true},
        // confirm that we have successfully opened the modal for handleClose
        () => (this.hasOpened = true)
      ), 0);
    }

    componentWillUnmount() {
      document.removeEventListener('backbutton', this.handleBack);
      document.body.removeEventListener('keydown', this.handleKeyDown);
      clearTimeout(this.closeTimer);
    }

    getTransitionDuration() {
      return this.props.effect.transition.duration;
    }

    render() {
      return (
        <ModalComponent
          onSuccess={this.handleSuccess}
          onCancel={this.handleCancel}
          onClickBackdrop={this.handleClickBackdrop}
          {...this.props.modalProps}
        >
          <BodyComponent
            onSuccess={this.handleSuccess}
            onCancel={this.handleCancel}
            onSubmit={this.handleSubmit}
          />
        </ModalComponent>
      );
    }
  };
}



// WEBPACK FOOTER //
// ./src/js/core-utilities/react-modals/ModalContainer.js